import { Suspense, lazy, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import dashboardService from '@api/v1/dashboardService'
import useReadState from '@hooks/useReadState'
import usePreferencesStore from '@store/preferencesStore'
import LoadingSpinner from '@components/common/LoadingSpinner'
import DashboardWidget from './DashboardWidget'
import DashboardAiSummary from './DashboardAiSummary'
import { toRglLayout, layoutToSaved } from '@utils/dashboardLayout'

// react-grid-layout renders BOTH the view (static) and the edit (drag/resize)
// grid, so what you arrange in Edit is exactly what you see — WYSIWYG. Lazy so
// the lib ships in an async chunk (only loaded when a dashboard renders).
const DashboardGrid = lazy(() => import('./DashboardGrid'))

/**
 * Renders one dashboard's widgets on a 12-col react-grid-layout — read-only in
 * VIEW mode, drag/resize in EDIT mode, both from the SAME coords (a saved
 * per-user override, else CB's stored GridStack coords) so Edit is WYSIWYG and
 * Save reflects exactly. The AI summary is PINNED above the grid.
 */
const DashboardView = ({ id }) => {
  const dashboardQuery = useQuery({
    queryKey: ['dashboard', id],
    queryFn: () => dashboardService.get(id),
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
  })
  const { data, error, refetch } = dashboardQuery

  /*
   * CC-53132 — three outcomes, not two. See `@api/v1/readState`.
   *
   * `isLoading` is `isPending && isFetching`, so it is FALSE for a query that is
   * pending but not in flight. react-query PAUSES a query while `onlineManager`
   * believes the browser is offline: `queryFn` is never called, nothing throws,
   * nothing logs, `data` stays undefined — and `data?.widgets || []` then turned
   * that into "This dashboard has no widgets.", with no spinner and no error, for
   * a dashboard nobody ever asked the server about. `retry: false` (above) means
   * it never self-corrects.
   */
  const dashboardState = useReadState('dashboard', dashboardQuery)

  const savedMap = usePreferencesStore((s) => s.dashboardLayouts[String(id)])
  const setDashboardLayout = usePreferencesStore((s) => s.setDashboardLayout)
  const resetDashboardLayout = usePreferencesStore((s) => s.resetDashboardLayout)

  const [editMode, setEditMode] = useState(false)
  const [draft, setDraft] = useState(null) // react-grid-layout array while editing

  // `dashboardService.get` is `res?.data || null` with no shape assertion, so a
  // 200 whose body lacks or renames `widgets` is NOT an empty dashboard either.
  // Keep the two apart: `widgetsResolved` says the body was readable, the array
  // is a shape fallback for the memos below (the service is shared with the tile
  // queries, so the guard belongs here rather than in it).
  const widgetsResolved = Array.isArray(data?.widgets)
  const rawWidgets = useMemo(() => (Array.isArray(data?.widgets) ? data.widgets : []), [data])
  // The persisted (or default) layout both view + edit render from.
  const savedLayout = useMemo(() => toRglLayout(rawWidgets, savedMap || null), [rawWidgets, savedMap])
  const layout = editMode ? (draft || savedLayout) : savedLayout

  const startEdit = () => { setDraft(savedLayout); setEditMode(true) }
  const cancelEdit = () => { setDraft(null); setEditMode(false) }
  const saveEdit = () => {
    setDashboardLayout(id, layoutToSaved(draft || savedLayout))
    setDraft(null)
    setEditMode(false)
  }
  // Revert to CB's default arrangement, reflected live in the draft.
  const resetLayout = () => { resetDashboardLayout(id); setDraft(toRglLayout(rawWidgets, null)) }

  // `idle` gets its OWN branch rather than riding with `loading`: here it can only
  // mean `enabled: !!id` is false, i.e. no dashboard is selected, and a falsy id
  // folded into the loading branch would be a spinner that never resolves. The
  // only host today renders this behind `{active && …}` (DashboardsPanel), which
  // is exactly why the safety belongs here and not in the caller.
  if (dashboardState === 'idle') {
    return <div className="text-center py-16 text-sm text-gray-500">Pick a dashboard to see its widgets.</div>
  }
  if (dashboardState === 'loading') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 flex justify-center">
        <LoadingSpinner message="Loading dashboard…" />
      </div>
    )
  }
  // The state that used to render as "this dashboard has no widgets". A paused
  // read sends no request, so there is nothing in any log to find — say it on
  // screen, and return BEFORE the AI summary: handing it zero widgets would have
  // it summarise nothing as if that were the dashboard.
  if (dashboardState === 'blocked') {
    return (
      <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        <p className="font-medium">Can’t reach the server, so we haven’t loaded this dashboard.</p>
        <p className="mt-1 text-amber-800">
          This is not an empty dashboard — your connection looks offline. Check it and try again.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
        >
          Try again
        </button>
      </div>
    )
  }
  if (dashboardState === 'failed') {
    return (
      <div role="alert" className="bg-red-50 border border-red-100 rounded-xl p-6 text-sm text-red-700">
        <p className="font-medium">Failed to load this dashboard: {error?.message}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Try again
        </button>
      </div>
    )
  }
  // Read completed, body unreadable — the `readShape.js` case, in this file's own
  // voice because the service hands back `null` instead of throwing.
  if (!widgetsResolved) {
    return (
      <div role="alert" className="bg-red-50 border border-red-100 rounded-xl p-6 text-sm text-red-700">
        <p className="font-medium">This dashboard came back in a shape we couldn’t read.</p>
        <p className="mt-1">
          This is not a dashboard with no widgets — try again, and report it if it persists.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Try again
        </button>
      </div>
    )
  }
  // Only now: a completed read whose body carried a real widgets array. This is
  // the one state in which "no widgets" is a fact about the dashboard.
  if (rawWidgets.length === 0) {
    return <div className="text-center py-16 text-sm text-gray-500">This dashboard has no widgets.</div>
  }

  return (
    <div className="animate-fade-up">
      {/* Pinned AI summary — a fixed feature, above (never inside) the tile grid.
          key={id} re-arms its grace period cleanly on dashboard switch. */}
      <DashboardAiSummary key={id} id={id} name={data?.title} widgets={rawWidgets} />

      {/* Layout toolbar */}
      <div className="flex items-center justify-end gap-2 mb-3">
        {!editMode ? (
          <button type="button" onClick={startEdit} className="btn-cb-toolbar">
            Edit layout
          </button>
        ) : (
          <>
            <span className="mr-auto text-xs text-gray-500">
              Drag a tile’s header to move, its corner to resize — or focus a tile and use arrow keys (Shift+arrows to resize).
            </span>
            <button type="button" onClick={resetLayout} className="btn-cb-toolbar">
              Reset to default
            </button>
            <button type="button" onClick={cancelEdit} className="btn-cb-toolbar">
              Cancel
            </button>
            <button type="button" onClick={saveEdit} className="btn-cb-save">
              Save layout
            </button>
          </>
        )}
      </div>

      <Suspense fallback={
        <div className="p-12 flex justify-center"><LoadingSpinner message="Loading dashboard…" /></div>
      }>
        <DashboardGrid
          widgets={rawWidgets}
          layout={layout}
          editable={editMode}
          onLayoutChange={setDraft}
          renderWidget={(w) => <DashboardWidget widget={w} />}
        />
      </Suspense>
    </div>
  )
}

export default DashboardView
